import { Injectable, ComponentFactoryResolver, ApplicationRef, Injector } from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { Constants } from 'src/app/constants/constants';
import { CoreService } from '../core/core.service';
import { StorageService } from '../storage/storage.service';
import { CustomReminderComponent } from 'src/app/modules/shared/components/custom-reminder/custom-reminder.component';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  private static readonly TASK_NOTIFICATIONS_KEY = 'taskNotifications';
  private static readonly LEAD_FOLLOWUP_NOTIFICATIONS_KEY = 'leadFollowupNotifications';
  private static readonly ELIGIBLE_LEAD_SUB_STATUS_IDS = [201, 203]; // Call Verified, Call Back
  private static readonly CALLBACK_CALL_SUB_DISPOSITION = 'call back (asked to call later)';

  private notificationSubject = new Subject<any>();
  private timeouts: { [key: string]: any } = {};
  private notifications = new Map<string, any>();
  private notificationQueue: any[] = [];


  constructor(
    private router: Router,
    private coreService: CoreService,
    private storageService: StorageService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector) {

    window.addEventListener('storage', (event) => {
      if (
        (event.key === CommonService.TASK_NOTIFICATIONS_KEY ||
          event.key === CommonService.LEAD_FOLLOWUP_NOTIFICATIONS_KEY) &&
        event.newValue
      ) {
        this.handleStorageEvent(event.key);
      }
    });

    this.notificationSubject.subscribe(notification => {
      this.showNotification(notification);
    });

    this.loadNotificationsFromStorage();
  }

  private getReminderKey(notification: any): string {
    if (notification?.reminderKey) {
      return notification.reminderKey;
    }
    if (notification?.type === 'lead' || notification?.leadId != null) {
      return `lead-${notification.leadId}`;
    }
    return `task-${notification.taskId}`;
  }

  private handleStorageEvent(storageKey: string) {
    const storedNotifications = this.storageService.getItem(storageKey);
    if (storedNotifications) {
      const notifications = JSON.parse(storedNotifications);
      if (Array.isArray(notifications)) {
        notifications.forEach((notification: any) => {
          const reminderKey = this.getReminderKey(notification);
          if (!this.notifications.has(reminderKey)) {
            this.notifications.set(reminderKey, notification);
            this.notificationSubject.next(notification);
          }
        });
      }
    }
  }

  private showNotification(notification: any) {
this.createCustomReminder(notification, true);
  }

  private createCustomReminder(notification: any, isTop: boolean, index: number = 0) {
  const reminderKey = this.getReminderKey(notification);
  const componentFactory = this.componentFactoryResolver.resolveComponentFactory(CustomReminderComponent);
  const componentRef = componentFactory.create(this.injector);
  componentRef.instance.message = notification.message;
  componentRef.instance.uniqueId = `custom-reminder-${reminderKey}-${Date.now()}`;
  componentRef.instance.isTop = isTop;
  componentRef.instance.onClose.subscribe(() => {
    this.appRef.detachView(componentRef.hostView);
    componentRef.destroy();

    const el = document.getElementById(componentRef.instance.uniqueId);
    if (el) el.remove();
    this.notificationQueue = this.notificationQueue.filter(
      n => this.getReminderKey(n) !== reminderKey
    );
    this.renderStackedNotifications();
  });

  this.appRef.attachView(componentRef.hostView);
  const domElem = (componentRef.hostView as any).rootNodes[0] as HTMLElement;
  domElem.setAttribute('id', componentRef.instance.uniqueId);
  domElem.classList.add(isTop ? 'top' : 'stacked');

  if (!isTop) {
    domElem.style.setProperty('--stack-offset', `${index * 12}px`);
  }

  document.querySelector('.custom-reminder-main')?.appendChild(domElem);
}


private renderStackedNotifications() {
  const wrapper = document.querySelector('.custom-reminder-main');
  if (!wrapper) return;

  wrapper.innerHTML = '';

  const total = this.notificationQueue.length;

  [...this.notificationQueue].reverse().forEach((notif, index) => {
    const reversedIndex = total - 1 - index;
    const isTop = index === total - 1;
    this.createCustomReminder(notif, isTop, reversedIndex);
  });
}

  private broadcastNotification(notification: any) {
  this.notificationQueue.push(notification);
  this.renderStackedNotifications();
}

scheduleNotification(task: any) {
  const now = new Date();
  const taskDueDate = new Date(task.remind_on);
  const notifyTime = new Date(taskDueDate.getTime() - 5 * 60000);

  if (notifyTime > now) {
    const timeout = notifyTime.getTime() - now.getTime();
    const reminderKey = `task-${task.id}`;

    if (this.timeouts[reminderKey]) {
      clearTimeout(this.timeouts[reminderKey]);
    }

    this.timeouts[reminderKey] = setTimeout(() => {
      const message = {
        task_name: task?.name ?? null,
        task_activity_name: task?.activity_name ?? null,
        task_time: moment(task.remind_on).format('hh:mm A') ?? null,
        task_program_name: task?.program_name ?? null,
        task_patient_name: task?.patient_name ?? null,
        task_member_id: task?.member_id ?? null,
        task_patient_mobile: task?.patient_mobile ?? null,
        patient_id: task?.patient_id ?? null,
        participation_id: task?.participation_id ?? null,
      };

      const notification = { taskId: task.id, message, notifyTime: notifyTime.toISOString() };
      this.updateTaskNotification(notification);
      this.broadcastNotification(notification);
    }, timeout);
  }
}

  isEligibleLeadFollowupReminder(lead: any, currentUserId?: number | null): boolean {
    if (!lead?.id || currentUserId == null) {
      return false;
    }

    // List API may omit lead_converter_id; callers must only pass leads for the current PA.
    if (
      lead.lead_converter_id != null &&
      Number(lead.lead_converter_id) !== Number(currentUserId)
    ) {
      return false;
    }

    const followUpAt = lead.next_follow_up_date || lead.remind_on;
    if (!followUpAt || !moment(followUpAt).isValid() || moment(followUpAt).isBefore(moment())) {
      return false;
    }

    const subStatusId = Number(lead.sub_status_id ?? lead.new_sub_status_id);
    if (CommonService.ELIGIBLE_LEAD_SUB_STATUS_IDS.includes(subStatusId)) {
      return true;
    }

    const callSubDisposition = String(lead.call_sub_disposition || '').trim().toLowerCase();
    if (callSubDisposition === CommonService.CALLBACK_CALL_SUB_DISPOSITION) {
      return true;
    }

    const subStatusName = String(lead.sub_status_name || '').trim().toLowerCase();
    return subStatusName === 'call back' || subStatusName === 'call verified';
  }

  scheduleLeadFollowupReminder(lead: any, currentUserId?: number | null): void {
    this.clearLeadFollowupReminder(lead?.id);

    if (!this.isEligibleLeadFollowupReminder(lead, currentUserId)) {
      return;
    }

    const followUpAt = lead.next_follow_up_date || lead.remind_on;
    const now = new Date();
    const followUpDate = new Date(followUpAt);
    const notifyTime = new Date(followUpDate.getTime() - 5 * 60000);

    if (notifyTime <= now) {
      return;
    }

    const timeout = notifyTime.getTime() - now.getTime();
    const reminderKey = `lead-${lead.id}`;

    if (this.timeouts[reminderKey]) {
      clearTimeout(this.timeouts[reminderKey]);
    }

    this.timeouts[reminderKey] = setTimeout(() => {
      const leadName =
        lead?.name ||
        [lead?.first_name, lead?.last_name].filter(Boolean).join(' ').trim() ||
        'Lead';
      const message = {
        type: 'lead',
        lead_id: lead.id,
        lead_name: leadName,
        lead_mobile: lead?.mobile_no || lead?.mobile || null,
        lead_follow_up_time: moment(followUpAt).format('DD-MM-YYYY hh:mm A'),
      };

      const notification = {
        type: 'lead',
        leadId: lead.id,
        reminderKey,
        message,
        notifyTime: notifyTime.toISOString(),
      };
      this.updateLeadFollowupNotification(notification);
      this.broadcastNotification(notification);
    }, timeout);
  }

  private updateTaskNotification(notification: any) {
    let notifications = this.storageService.getItem(CommonService.TASK_NOTIFICATIONS_KEY);
    notifications = notifications ? JSON.parse(notifications) : [];
    if (!Array.isArray(notifications)) {
      notifications = [];
    }

    notifications = notifications.filter((notif: any) => notif.taskId !== notification.taskId);

    notifications.push(notification);

    this.storageService.setItem(CommonService.TASK_NOTIFICATIONS_KEY, JSON.stringify(notifications));
    this.notifications.set(this.getReminderKey(notification), notification);
  }

  private updateLeadFollowupNotification(notification: any) {
    let notifications = this.storageService.getItem(CommonService.LEAD_FOLLOWUP_NOTIFICATIONS_KEY);
    notifications = notifications ? JSON.parse(notifications) : [];
    if (!Array.isArray(notifications)) {
      notifications = [];
    }

    notifications = notifications.filter((notif: any) => notif.leadId !== notification.leadId);
    notifications.push(notification);

    this.storageService.setItem(
      CommonService.LEAD_FOLLOWUP_NOTIFICATIONS_KEY,
      JSON.stringify(notifications)
    );
    this.notifications.set(this.getReminderKey(notification), notification);
  }

  clearNotification(taskId: number) {
    let notifications = this.storageService.getItem(CommonService.TASK_NOTIFICATIONS_KEY);
    notifications = notifications ? JSON.parse(notifications) : [];
    if (!Array.isArray(notifications)) {
      notifications = [];
    }

    notifications = notifications.filter((notification: any) => notification.taskId !== taskId);
    this.storageService.setItem(CommonService.TASK_NOTIFICATIONS_KEY, JSON.stringify(notifications));

    const reminderKey = `task-${taskId}`;
    if (this.timeouts[reminderKey]) {
      clearTimeout(this.timeouts[reminderKey]);
      delete this.timeouts[reminderKey];
    }

    this.notifications.delete(reminderKey);
  }

  clearLeadFollowupReminder(leadId: number): void {
    if (!leadId) {
      return;
    }

    let notifications = this.storageService.getItem(CommonService.LEAD_FOLLOWUP_NOTIFICATIONS_KEY);
    notifications = notifications ? JSON.parse(notifications) : [];
    if (!Array.isArray(notifications)) {
      notifications = [];
    }

    notifications = notifications.filter((notification: any) => notification.leadId !== leadId);
    this.storageService.setItem(
      CommonService.LEAD_FOLLOWUP_NOTIFICATIONS_KEY,
      JSON.stringify(notifications)
    );

    const reminderKey = `lead-${leadId}`;
    if (this.timeouts[reminderKey]) {
      clearTimeout(this.timeouts[reminderKey]);
      delete this.timeouts[reminderKey];
    }

    this.notifications.delete(reminderKey);

    const queueLengthBefore = this.notificationQueue.length;
    this.notificationQueue = this.notificationQueue.filter(
      n => this.getReminderKey(n) !== reminderKey
    );
    // Avoid rebuilding the toast stack when only a pending timeout was cleared.
    if (this.notificationQueue.length !== queueLengthBefore) {
      this.renderStackedNotifications();
    }
  }


  clearAllNotifications(): void {
  this.notifications.clear();
  this.storageService.removeItem(CommonService.TASK_NOTIFICATIONS_KEY);
  this.storageService.removeItem(CommonService.LEAD_FOLLOWUP_NOTIFICATIONS_KEY);
  for (const reminderKey in this.timeouts) {
    clearTimeout(this.timeouts[reminderKey]);
    delete this.timeouts[reminderKey];
  }
  const wrapper = document.querySelector('.custom-reminder-main');
  if (wrapper) {
    wrapper.innerHTML = '';
  }

  this.notificationQueue = [];
}


  loadNotificationsFromStorage(): void {
    this.loadRemindersFromStorage(CommonService.TASK_NOTIFICATIONS_KEY);
    this.loadRemindersFromStorage(CommonService.LEAD_FOLLOWUP_NOTIFICATIONS_KEY);
  }

  private loadRemindersFromStorage(storageKey: string): void {
    const storedNotifications = this.storageService.getItem(storageKey);
    if (!storedNotifications) {
      return;
    }

    const notifications = JSON.parse(storedNotifications);
    if (!Array.isArray(notifications)) {
      return;
    }

    notifications.forEach((notification: any) => {
      const now = new Date();
      const notifyTime = new Date(notification.notifyTime);
      if (notifyTime > now) {
        const timeout = notifyTime.getTime() - now.getTime();
        const reminderKey = this.getReminderKey(notification);
        if (this.timeouts[reminderKey]) {
          clearTimeout(this.timeouts[reminderKey]);
        }
        this.timeouts[reminderKey] = setTimeout(() => {
          this.broadcastNotification(notification);
        }, timeout);
      }
    });
  }

  isObjectEmpty(obj: any): boolean {
    return Object.values(obj).length === 0;
  }

  getPatientIdAndParticipationId(): any {
    const url: string = this.router.url;
    const urlArr: string[] = url.split('/');
    return {
      patientId: Number(urlArr[3]),
      participationId: Number(urlArr[4]),
    };
  }

  getUTCFormat(date: any): string {
    let d: any = moment.tz(date, 'DD-MM-YYYY HH:mm', Constants.TIMEZONE).format();
    return d;
  }

  // * This function returns string format if date format is given
  // * Otherwise it returns Date timestamp
  getUserTimezoneFormat(date: any, timezone: string, format: string | null): any{
    let convertedTimezone: any = (format ? moment.tz(date, timezone).format(format) : moment.tz(date, timezone).format());
    return convertedTimezone;
  }

  searchTask(payload: any): Observable<any> {
    return this.coreService.callPostApi('common/search_task', payload);
  }

   generateErrorText(obj: any, errorField: any): string {
    if (errorField?.errors?.['required']) {
      return 'This field is required!';
    } else if (errorField?.errors?.['max'] || errorField?.errors?.['min']) {
      return `Value should be between ${obj.min} and ${obj.max}`;
    } else if(errorField?.errors?.['pattern']) {
      return `Invalid Character`
    } else {
      return "Validation failed!";
    }
  }

  atLeastOneValidator(keyArray: any): any {
    return (controlGroup: any) => {
      let controls = controlGroup.controls;
      if (controls) {
        let index = keyArray.findIndex((key: any) => controls[key].value !== '');
        if (index == -1) {
          return {
            atLeastOneRequired: {
              text: 'At least one value is required'
            }
          }
        }
      }
      return null;
    };
  };

  private selectedMseIdSubject = new BehaviorSubject<any | null>(null);
  selectedMseId$ = this.selectedMseIdSubject.asObservable();
  setSelectedMseId(mseId: any) {
    this.selectedMseIdSubject.next(mseId);
  }

  /* Grab only published programs out of all the programs created */
  grabPublishedPrograms(programsObj: any): any{
    const publishedPrograms: any = {};
    if(!this.isObjectEmpty(programsObj)){
      Object.values(programsObj).forEach((program: any) => {
        if(program.status_id === 1) publishedPrograms[program.program_id] = program;
      });
    }

    return publishedPrograms;
  }
}
